import type {PullRequestPickerPullRequest} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import {LazyPullRequestAndBranchPicker} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import {
  fetchRepository,
  fetchTopRepositories,
  RepositoryPicker,
  type RepositoryPickerRepository,
} from '@github-ui/item-picker/RepositoryPicker'
import type {BranchPickerBranch} from '@github-ui/item-picker-branch-relay/BranchPicker'
import {useSuspenseQuery} from '@github-ui/react-query'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {GlobalCommands} from '@github-ui/ui-commands'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {IconButton, Truncate} from '@primer/react'
import {type HTMLAttributes, type ReactElement, useCallback, useEffect, useMemo, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {ERRORS} from '../../../constants/errors'
import {LABELS} from '../../../constants/labels'
import {linkBranchesMutation} from '../../../mutations/link-branches'
import {linkPullRequestsMutation} from '../../../mutations/link-pull-requests-mutation'
import styles from './DevelopmentPicker.module.css'
import {
  developmentPickerQueryKey,
  type DevelopmentPickerRepository,
  fetchDevelopmentPicker,
} from './fetch-development-picker'

export type LazyDevelopmentPickerBaseProp = {
  issueId: string
  // Identifies the issue this picker was opened from, for the `pull_request_and_branch_picker_search`
  // endpoint route; unrelated to the repo the user may pick to search within.
  issueOwner: string
  issueRepo: string
  issueNumber: number
  linkedBranches: BranchPickerBranch[]
  linkedPullRequests: PullRequestPickerPullRequest[]
  anchorElement: (props: HTMLAttributes<HTMLElement>) => React.ReactElement
  isCreateBranchDialogOpen: boolean
  repoPickerSubtitle: ReactElement
  prAndBranchPickerSubtitle: ReactElement
  triggerOpen?: boolean
  onSelection: () => void
}

export type LazyDevelopmentPickerProps = LazyDevelopmentPickerBaseProp

export type LazyDevelopmentPickerInternalProps = LazyDevelopmentPickerBaseProp & {
  initialRepository: RepositoryPickerRepository | null
  topRepositories: RepositoryPickerRepository[]
  /**
   * Serves as default title while preloading the other data
   */
  repoNameWithOwner?: string
  /**
   * Override for picker's loading state to support pre-fetching
   */
  isLoading?: boolean
}

/**
 * Wrapper component for lazy picker to only start fetching when the picker is opened
 */
export function LazyDevelopmentPickerOnClick({anchorElement, ...props}: LazyDevelopmentPickerProps) {
  const [wasTriggered, setWasTriggered] = useState(false)

  if (!wasTriggered && anchorElement) {
    return (
      <>
        <GlobalCommands commands={{'item-pickers:open-development': () => setWasTriggered(true)}} />
        {anchorElement({onClick: () => setWasTriggered(true)})}
      </>
    )
  }

  return <LazyDevelopmentPickerTanStackData anchorElement={anchorElement} triggerOpen={wasTriggered} {...props} />
}

const LazyDevelopmentPickerTanStackData = ({
  issueId,
  issueOwner,
  issueRepo,
  issueNumber,
  ...rest
}: LazyDevelopmentPickerProps) => {
  const {data: repository} = useSuspenseQuery({
    queryKey: developmentPickerQueryKey(issueOwner, issueRepo, issueNumber),
    queryFn: ({signal}) => fetchDevelopmentPicker(issueOwner, issueRepo, issueNumber, signal),
  })

  return (
    <LazyDevelopmentPickerData
      repository={repository}
      issueId={issueId}
      issueOwner={issueOwner}
      issueRepo={issueRepo}
      issueNumber={issueNumber}
      {...rest}
    />
  )
}

const LazyDevelopmentPickerData = ({
  repository,
  isLoading: isPickerQueryLoading = false,
  ...rest
}: LazyDevelopmentPickerProps & {
  repository: DevelopmentPickerRepository | null
  isLoading?: boolean
}) => {
  const environment = useRelayEnvironment()
  const [initialRepository, setInitialRepository] = useState<RepositoryPickerRepository | null>(null)
  const [topRepositories, setTopRepositories] = useState<RepositoryPickerRepository[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    async function loadData() {
      if (isLoading || !repository || initialRepository) return

      setIsLoading(true)
      const initialRepo = await fetchRepository(environment, repository.owner.login, repository.name)
      setInitialRepository(initialRepo)

      const data = await fetchTopRepositories(environment)
      setTopRepositories(data)
      setIsLoading(false)
    }

    loadData()

    // intentional to run this only once on mount
  }, [environment, initialRepository, isLoading, repository, topRepositories])

  return (
    <LazyDevelopmentPickerInternal
      repoNameWithOwner={repository?.nameWithOwner}
      initialRepository={initialRepository}
      topRepositories={topRepositories}
      isLoading={isPickerQueryLoading || isLoading}
      {...rest}
    />
  )
}

type PickerType = 'repos' | 'pulls-branches'
const LazyDevelopmentPickerInternal = ({
  issueId,
  issueOwner,
  issueRepo,
  issueNumber,
  initialRepository,
  topRepositories,
  anchorElement,
  repoPickerSubtitle,
  repoNameWithOwner: initialRepoNameWithOwner,
  prAndBranchPickerSubtitle,
  isCreateBranchDialogOpen,
  linkedBranches,
  linkedPullRequests,
  triggerOpen,
  isLoading,
  onSelection,
}: LazyDevelopmentPickerInternalProps) => {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const [initialRepoLoading, setInitialRepoLoading] = useState(true)
  const [selectedRepository, setSelectedRepository] = useState<RepositoryPickerRepository | null>(null)
  const [activePicker, setActivePicker] = useState<PickerType>('pulls-branches')
  const [repoPickerOpen, setRepoPickerOpen] = useState(false)
  const [pullsPickerOpen, setPullsPickerOpen] = useState(triggerOpen ?? false)

  /**
   * Persist loading until current and top repos are preloaded
   */
  useEffect(() => {
    if (initialRepoLoading && initialRepository !== null && topRepositories !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedRepository(initialRepository)
      setInitialRepoLoading(false)
    }
  }, [initialRepoLoading, initialRepository, topRepositories])

  const openPicker = useCallback(
    (picker?: PickerType) => {
      switch (picker ?? activePicker) {
        case 'repos':
          setRepoPickerOpen(true)
          setPullsPickerOpen(false)
          break
        case 'pulls-branches':
          setPullsPickerOpen(true)
          setRepoPickerOpen(false)
          break
      }
    },
    [activePicker],
  )

  const onSelectRepository = useCallback(
    (repo: RepositoryPickerRepository | null) => {
      setSelectedRepository(repo)
      setActivePicker('pulls-branches')
      openPicker('pulls-branches')
    },
    [openPicker],
  )

  const repoNameWithOwner = useMemo(() => {
    if (initialRepoLoading) return initialRepoNameWithOwner ?? ''

    return selectedRepository ? selectedRepository.nameWithOwner : ''
  }, [initialRepoLoading, initialRepoNameWithOwner, selectedRepository])

  const linkedPullRequestsForRepo = useMemo(() => {
    return linkedPullRequests.filter(pr => pr.repository.id === selectedRepository?.id)
  }, [linkedPullRequests, selectedRepository?.id])

  const linkedBranchesForRepo = useMemo(() => {
    return linkedBranches.filter(branch => branch.repository.id === selectedRepository?.id)
  }, [linkedBranches, selectedRepository?.id])

  const handleClickRepoPicker = () => {
    setSelectedRepository(null)
    setActivePicker('repos')
    openPicker('repos')
  }

  const NavigateRepoPickerHeading = (
    <div className={styles.navigationHeader}>
      <IconButton
        variant="invisible"
        icon={ArrowLeftIcon}
        size="small"
        aria-label={LABELS.development.repositoryPickerNav}
        onClick={handleClickRepoPicker}
        className={styles.IconButton}
      />
      <Truncate title={repoNameWithOwner} className={styles.Truncate}>
        {repoNameWithOwner}
      </Truncate>
    </div>
  )

  const onSelectionChange = useCallback(
    (selected: Array<PullRequestPickerPullRequest | BranchPickerBranch>) => {
      const newItems = selected.filter(
        item =>
          [...linkedPullRequestsForRepo, ...linkedBranchesForRepo].find(prOrBranch => prOrBranch.id === item.id) ===
          undefined,
      )
      const existingItems = [...linkedPullRequests, ...linkedBranches].filter(
        item =>
          item.repository.id !== selectedRepository?.id ||
          selected.find(prOrBranch => prOrBranch.id === item.id) !== undefined,
      )

      const sortedItems = [...existingItems, ...newItems]
      const sortedPrs = sortedItems.filter(item => item.__typename === 'PullRequest')
      const sortedBranches = sortedItems.filter(item => item.__typename === 'Ref')

      linkPullRequestsMutation({
        environment,
        input: {
          baseIssueOrPullRequestId: `${issueId}`,
          linkingIds: sortedPrs.map(pr => pr.id),
        },
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLinkedPullRequests,
          })
        },
        onCompleted: onSelection,
        linkingPrs: sortedPrs,
      })

      linkBranchesMutation({
        environment,
        input: {
          baseIssueId: `${issueId}`,
          linkingIds: sortedBranches.map(branch => branch.id),
        },
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLinkedBranches,
          })
        },
        linkingBranches: sortedBranches as BranchPickerBranch[],
      })
    },
    [
      addToast,
      environment,
      issueId,
      linkedBranches,
      linkedBranchesForRepo,
      linkedPullRequests,
      linkedPullRequestsForRepo,
      selectedRepository?.id,
      onSelection,
    ],
  )

  return (
    <>
      <GlobalCommands commands={{'item-pickers:open-development': () => openPicker()}} />
      {/* Due to the internal item picker context and open/close management, not rendering the picker would
          result in a new item being stored on context every time there's a switch between the pickers. */}
      <div hidden={activePicker !== 'repos'}>
        <RepositoryPicker
          initialRepository={undefined}
          topRepositories={topRepositories}
          onSelect={repo => onSelectRepository(repo ?? null)}
          anchorElement={anchorElement}
          title={'Link a branch or pull request'}
          subtitle={repoPickerSubtitle}
          preventClose={isCreateBranchDialogOpen}
          preventDefault
          triggerOpen={repoPickerOpen}
          onOpen={() => setRepoPickerOpen(true)}
          onClose={() => setRepoPickerOpen(false)}
        />
      </div>
      <div hidden={activePicker !== 'pulls-branches'}>
        <LazyPullRequestAndBranchPicker
          repoNameWithOwner={repoNameWithOwner}
          issueOwner={issueOwner}
          issueRepo={issueRepo}
          issueNumber={issueNumber}
          initialSelectedPrs={linkedPullRequestsForRepo}
          initialSelectedBranches={linkedBranchesForRepo}
          onSelectionChange={onSelectionChange}
          anchorElement={anchorElement}
          title={NavigateRepoPickerHeading}
          subtitle={prAndBranchPickerSubtitle}
          // Explicitly disabling it as this complex picker manages its own shortcuts
          shortcutsEnabled={false}
          triggerOpen={pullsPickerOpen}
          onOpen={() => setPullsPickerOpen(true)}
          onClose={() => setPullsPickerOpen(false)}
          preventClose={isCreateBranchDialogOpen}
          loading={isLoading}
        />
      </div>
    </>
  )
}
